import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormGroup, FormLabel, Grid, IconButton, ListItemText, MenuItem, TextField, Tooltip, useTheme } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { RecencyReportFilter, Report, ReportFilterTargetFields, TimeUnits } from "../../api/dtos";

interface IRecencyReportFilterSelectProps {
    report?: Report,
    applicableFields: ReportFilterTargetFields[],
    requestRecencyFilters?: RecencyReportFilter[],
    recencyReportFilters?: RecencyReportFilter[],
    setRecencyReportFilters: React.Dispatch<React.SetStateAction<RecencyReportFilter[] | undefined>>,
}

const RecencyReportFilterSelect:FC<IRecencyReportFilterSelectProps> = ({
    applicableFields,
    requestRecencyFilters,
    recencyReportFilters,
    setRecencyReportFilters
}) => {
    const theme = useTheme();

    const [amount, setAmount] = useState<number | undefined>();
    const [unit, setUnit] = useState<TimeUnits | undefined>();

    const sourceFilters = requestRecencyFilters ?? recencyReportFilters;
    const fieldKey = applicableFields.join(',');
    const isCurrentPeriod = unit === TimeUnits.FinancialYear || unit === TimeUnits.Quarter;
    const publishedFilters = useRef(new WeakMap<RecencyReportFilter[], string>());

    useEffect(() => {
        // Parent feedback must not clear a rolling-period draft before its quantity has been entered.
        if (sourceFilters && publishedFilters.current.get(sourceFilters) === fieldKey) return;
        const existing = sourceFilters?.find(filter =>
            filter.filterTargetField !== undefined && applicableFields.includes(filter.filterTargetField)
        );
        setAmount(existing?.quantity);
        setUnit(existing?.unit);
    }, [sourceFilters, fieldKey]);

    const updateFilters = (nextAmount: number | undefined, nextUnit: TimeUnits | undefined) => {
        const currentPeriod = nextUnit === TimeUnits.FinancialYear || nextUnit === TimeUnits.Quarter;
        const complete = nextUnit !== undefined && (currentPeriod || nextAmount !== undefined);
        setRecencyReportFilters(current => {
            const filters = [
                ...(current ?? []).filter(filter =>
                    filter.filterTargetField === undefined || !applicableFields.includes(filter.filterTargetField)
                ),
                ...(complete ? applicableFields.map(field => new RecencyReportFilter({
                    ...current?.find(filter => filter.filterTargetField === field),
                    filterTargetField: field,
                    quantity: currentPeriod ? undefined : nextAmount,
                    unit: nextUnit,
                })) : []),
            ];
            publishedFilters.current.set(filters, fieldKey);
            return filters;
        });
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = event.target.value === '' ? undefined : Number(event.target.value);
        if (next !== undefined && !Number.isFinite(next)) return;
        setAmount(next);
        updateFilters(next, unit);
    };

    const handleUnitChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = event.target.value === '' ? undefined : Number(event.target.value) as TimeUnits;
        setUnit(next);
        updateFilters(amount, next);
    };
    return (
        <>
            <FormGroup>
                <FormLabel 
                    component={"legend"}
                    sx={{
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    Period
                    <Tooltip 
                        title="Choose a quantity for Days, Weeks, Months or Years. Financial Year and Quarter use the full current period."
                    >
                        <IconButton 
                            size="small"
                            sx={{
                                color: theme.palette.secondary.light
                            }}
                        >
                            <FontAwesomeIcon icon={faCircleQuestion} />
                        </IconButton>
                    </Tooltip>
                </FormLabel>
                <Grid
                    container
                    gap={2}
                    sx={{
                        mt: 1
                    }}
                >
                    {!isCurrentPeriod && <TextField
                        inputMode="numeric"
                        onChange={handleAmountChange}
                        label={"Quantity"}
                        value={amount ?? ""}
                    />}
                    <TextField
                        select
                        onChange={handleUnitChange}
                        label={"Unit"}
                        value={unit ?? ""}
                        slotProps={{
                            input: {
                                sx: {
                                    height: 56
                                }
                            }
                        }}
                        sx={{
                            width: 200
                        }}
                    >
                        <MenuItem id={""} value={""}><ListItemText primary={" "} /></MenuItem>
                        <MenuItem id={TimeUnits.Days.toString()} value={TimeUnits.Days}><ListItemText primary={"Days"}/></MenuItem>
                        <MenuItem id={TimeUnits.Weeks.toString()} value={TimeUnits.Weeks}><ListItemText primary={"Weeks"}/></MenuItem>
                        <MenuItem id={TimeUnits.Months.toString()} value={TimeUnits.Months}><ListItemText primary={"Months"}/></MenuItem>
                        <MenuItem id={TimeUnits.Years.toString()} value={TimeUnits.Years}><ListItemText primary={"Years"}/></MenuItem>
                        <MenuItem id={TimeUnits.FinancialYear.toString()} value={TimeUnits.FinancialYear}><ListItemText primary="Financial Year" /></MenuItem>
                        <MenuItem id={TimeUnits.Quarter.toString()} value={TimeUnits.Quarter}><ListItemText primary="Quarter" /></MenuItem>
                    </TextField>
                </Grid>
            </FormGroup>
        </>
    );
}

export default RecencyReportFilterSelect;
